import { Fragment, ReactElement } from 'react';
import { generatePath } from 'react-router-dom';

import styled from 'styled-components';

import { Collapse } from '@components/animators/collapse';
import { Button } from '@components/button';
import { IconProps } from '@components/icons';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconCheckmark } from '@components/icons/checkmark';
import { IconDropdown } from '@components/icons/dropdown';
import { IconHelp } from '@components/icons/help';
import { IconJourneyItem } from '@components/icons/journey-icon';
import { IconOpenNewTab } from '@components/icons/open-new-tab';
import { IconTeam } from '@components/icons/team';
import { ProgressBar } from '@components/progress-bar';
import { CloseButton, Sidebar, hideSidebar, useSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { SelectionStore, createSelectionStore } from '@stores/create-selection-store';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { config } from 'app-config';

import { useCurrentCompany } from 'api/authentication/hooks';
import { StepKey } from 'api/authentication/requests';
import {
  useCompleteOnboarding,
  useCompleteOnboardingStep,
  useSkipOnboarding,
} from 'api/company/hooks';

import { routePaths } from '../navigation';

export const useCompanyOnboardingSidebar = ({ onHide }: { onHide: () => void }) => {
  return useSidebar(CompanyOnboardingSidebar, {
    variant: 'REGULAR',
    backgroundColor: 'gradient',
    closeOnClickOutside: true,
    onHide,
  });
};

type StepType = {
  name: StepKey;
  label: string;
  description?: string;
  isCompleted: boolean;
  link?: string;
  isLinkExternal?: boolean;
  completeStep?: () => void;
  buttonLabel?: string;
  buttonIcon?: ReactElement<IconProps>;
};

const getExpandedStore = (persistKey: string) => {
  return createSelectionStore({
    persistKey: persistKey,
    defaultState: {
      runDemoJourney: false,
      explorePortal: true,
      exploreTemplates: true,
      publishJourney: true,
      inviteTeammate: true,
    },
  });
};

const CompanyOnboardingSidebar = () => {
  const { t } = useScopedTranslate(locale);
  const company = useCurrentCompany();
  const skipOnboarding = useSkipOnboarding();
  const completeOnboarding = useCompleteOnboarding();
  const expandedStore = getExpandedStore(`company-onboarding-state-${company?.shortId}`);
  const handleSkipOnboarding = async () => {
    skipOnboarding.mutateAsync();
    hideSidebar();
  };
  const handleCompleteOnboarding = async () => {
    completeOnboarding.mutateAsync();
    hideSidebar();
  };
  const completeOnboardingStep = useCompleteOnboardingStep();

  const portalDomain = company?.portalDomain
    ? `https://${company.portalDomain}`
    : `${config.PORTAL_URL}/${company?.slug}/`;

  const onboardingStatus = company?.onboarding?.status;
  const onboardingSteps = onboardingStatus?.steps;

  const journeyTravellerPath = generatePath(routePaths.traveller, {
    travellerId: onboardingStatus?.config.travellerJourneyId ?? '',
    journeyId: onboardingStatus?.config.journeyId ?? '',
    workspaceId: onboardingStatus?.config.homeWorkspaceId ?? '',
    '*': null,
  });

  const steps: StepType[] = [
    {
      name: 'createAccount',
      label: t('createAnAccount'),
      isCompleted: true,
    },
    {
      name: 'runDemoJourney',
      label: t('runDemoJourney'),
      description: t('runDemoJourneyDescription'),
      isCompleted: onboardingSteps?.runDemoJourney ?? false,
      link: journeyTravellerPath,
      isLinkExternal: false,
      buttonLabel: t('runDemoJourneyButtonLabel'),
      buttonIcon: <IconJourneyItem />,
    },
    {
      name: 'explorePortal',
      label: t('explorePortal'),
      description: t('explorePortalDescription'),
      isCompleted: onboardingSteps?.explorePortal ?? false,
      link: portalDomain,
      isLinkExternal: true,
      buttonLabel: t('explorePortalButtonLabel'),
      completeStep: () => completeOnboardingStep('explorePortal'),
    },
    {
      name: 'exploreTemplates',
      label: t('exploreTemplates'),
      description: t('exploreTemplatesDescription'),
      isCompleted: onboardingSteps?.exploreTemplates ?? false,
      link: 'https://www.50skills.com/templates',
      isLinkExternal: true,
      buttonLabel: t('exploreTemplatesButtonLabel'),
      completeStep: () => completeOnboardingStep('exploreTemplates'),
    },
    {
      name: 'publishJourney',
      label: t('publishJourney'),
      description: t('publishJourneyDescription'),
      isCompleted: onboardingSteps?.publishJourney ?? false,
      link: generatePath(routePaths.createJourney, {
        workspaceId: company?.onboarding?.status.config.homeWorkspaceId ?? '',
        '*': null,
      }),
      isLinkExternal: false,
      buttonLabel: t('publishJourneyButtonLabel'),
      buttonIcon: <IconAddPlus />,
    },
    {
      name: 'inviteTeammate',
      label: t('inviteTeammate'),
      description: t('inviteTeammateDescription'),
      isCompleted: onboardingSteps?.inviteTeammate ?? false,
      link: generatePath(routePaths.settingsUsers),
      isLinkExternal: false,
      buttonLabel: t('inviteTeammateButtonLabel'),
      buttonIcon: <IconTeam />,
    },
  ];

  const ratio = company?.onboarding?.ratio ?? 0;

  return (
    <Sidebar>
      <Sidebar.Header>
        <Stack orientation="horizontal" justify="space-between" align="center">
          <Stack orientation="horizontal" space={2}>
            <IconHelp color="indigo100" />
            <Text font="bold18" color="textPrimary" text={t('gettingStarted')} />
          </Stack>
          <CloseButton />
        </Stack>
      </Sidebar.Header>
      <Sidebar.Content>
        <Stack space={4}>
          <Text font="regular14" color="textSecondary" text={t('welcome')} />
          <ProgressBar variant="info" ratio={ratio} />
          <Sidebar.Separator />
          {steps.map((step, index) => (
            <Fragment key={step.name}>
              <OnboardingItem step={step} id={index} expandedStore={expandedStore} />
              <Sidebar.Separator />
            </Fragment>
          ))}
          {ratio < 1 ? (
            <Stack orientation="horizontal" justify="flex-end">
              <Button
                variant="secondary"
                text={t('skipButtonLabel')}
                onClick={handleSkipOnboarding}
                iconLeft={<IconCheckmark />}
              />
            </Stack>
          ) : (
            <Stack space={4}>
              <Text font="regular14" color="textSecondary" text={t('allStepsCompleted')} />
              <Stack orientation="horizontal" justify="flex-end">
                <Button
                  variant="primary"
                  text={t('closeButtonLabel')}
                  onClick={handleCompleteOnboarding}
                />
              </Stack>
            </Stack>
          )}
        </Stack>
      </Sidebar.Content>
    </Sidebar>
  );
};

const OnboardingItem = ({
  step,
  id,
  expandedStore,
}: {
  step: StepType;
  id: number;
  expandedStore: SelectionStore;
}) => {
  const {
    name,
    label,
    description,
    isCompleted,
    link,
    isLinkExternal,
    completeStep,
    buttonLabel,
    buttonIcon,
  } = step;
  const isCollapsed = expandedStore.useStore((state) => state[name]);
  return (
    <Stack space={4}>
      <OnboardingItemContainer
        onClick={() => expandedStore.toggle(name)}
        data-clickable={description ? true : false}
      >
        <Stack orientation="horizontal" space={2}>
          <OnboardingItemStep data-completed={isCompleted}>
            <Text font="semibold12" color="textPrimary" text={isCompleted ? '√' : id + 1} />
          </OnboardingItemStep>
          <Text
            font="bold14"
            color={isCompleted ? 'elementGreen100' : 'elementPurple50'}
            text={label}
          />
        </Stack>
        {description && (
          <IconContainer>
            <IconDropdown color="purple50" rotateDegrees={isCollapsed ? -90 : 0} />
          </IconContainer>
        )}
      </OnboardingItemContainer>
      {description && (
        <Collapse isCollapsed={isCollapsed}>
          <Stack space={4}>
            {description && <Text font="regular14" color="textSecondary" text={description} />}
            {link && (
              <Button
                variant="secondary"
                size="sm"
                text={buttonLabel}
                href={link}
                onClick={completeStep ?? undefined}
                iconLeft={buttonIcon}
                iconRight={isLinkExternal ? <IconOpenNewTab size={16} /> : undefined}
                target={isLinkExternal ? '_blank' : undefined}
              />
            )}
          </Stack>
        </Collapse>
      )}
    </Stack>
  );
};

const OnboardingItemContainer = styled(Stack).attrs({
  orientation: 'horizontal',
  justify: 'space-between',
  align: 'center',
})({
  '&[data-clickable="true"]': {
    cursor: 'pointer',
  },
});

const OnboardingItemStep = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '50%',
  height: pxToRem(24),
  width: pxToRem(24),
  color: theme.color.white100,
  backgroundColor: theme.color.elementPurple50,
  '&[data-completed="true"]': {
    backgroundColor: theme.color.elementGreen100,
  },
});

const IconContainer = styled.span({
  display: 'flex',
  transition: 'transform 150ms ease-out',
  padding: `${pxToRem(0)} ${pxToRem(8)}`,
});

const locale = {
  en: {
    gettingStarted: 'Getting Started',
    welcome:
      'Welcome to Journeys! Get familiar with how it works by completing the following tasks',
    createAnAccount: 'Create an account',
    runDemoJourney: 'Run demo journey',
    runDemoJourneyDescription:
      'Send yourself on a demo journey to quickly master the basics of the system',
    runDemoJourneyButtonLabel: 'Open demo journey',
    explorePortal: 'Explore portal',
    explorePortalDescription:
      'Explore the ready-made company portal to discover public forms that initiate journeys',
    explorePortalButtonLabel: 'Open portal',
    exploreTemplates: 'Explore templates',
    exploreTemplatesDescription:
      'Browse our colloection of pre-built journey templates for inspiration and faster setup',
    exploreTemplatesButtonLabel: 'Explore templates',
    publishJourney: 'Publish a new journey',
    publishJourneyDescription:
      'Design and launch your own journey by creating a workflow, adding actions, and hitting publish',
    publishJourneyButtonLabel: 'Create new journey',
    inviteTeammate: 'Invite a teammate',
    inviteTeammateDescription:
      'Optionally invite colleagues to explore and build journeys alongside you',
    inviteTeammateButtonLabel: 'Teammates',
    skipButtonLabel: 'Skip getting started guide',
    allStepsCompleted:
      'You have completed all steps in the getting started guide, now go make some workflow magic ✨',
    closeButtonLabel: 'Close getting started guide',
  },
};
